import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import ArtistCard from "../components/ArtistCard";
import { FALLBACK_CATEGORIES, FALLBACK_CITIES } from "../constants/mcevents";
import { useContent } from "../contexts/ContentContext";
import { Search, Filter, X } from "lucide-react";

export default function Explore() {
  const { t } = useContent();
  const [params, setParams] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const category = params.get("category") || "";
  const city = params.get("city") || "";

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const search = new URLSearchParams();
      if (category) search.set("category", category);
      if (city) search.set("city", city);
      if (params.get("q")) search.set("q", params.get("q"));
      const { data } = await api.get(`/artists?${search.toString()}`);
      setArtists(data.artists || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchArtists(); /* eslint-disable-next-line */ }, [category, city, params.get("q")]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setFilter("q", q);
  };

  const hasFilters = category || city || params.get("q");

  return (
    <div className="container-mc py-12" data-testid="explore-page">
      <div className="mb-8">
        <p className="overline mb-2">Catalogue · Artistes guinéens</p>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Trouvez l'artiste parfait</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">{artists.length} artistes disponibles à travers la Guinée.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar filters */}
        <aside className="lg:col-span-3 card-mc p-6 h-fit lg:sticky lg:top-24" data-testid="filters-panel">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              <span className="font-display font-bold">Filtres</span>
            </div>
            {hasFilters && (
              <button onClick={() => setParams({})} data-testid="reset-filters" className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-[hsl(var(--primary))]">
                <X className="w-3 h-3 inline" /> Réinit.
              </button>
            )}
          </div>

          <form onSubmit={onSearch} className="mb-6">
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Recherche</label>
            <div className="mt-2 flex items-center border border-border bg-card">
              <input
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nom, style..."
                className="flex-1 px-3 py-2 bg-transparent outline-none text-sm"
              />
              <button type="submit" data-testid="search-submit" className="px-3 border-l border-border hover:bg-muted">
                <Search className="w-4 h-4" />
              </button>
            </div>
          </form>

          <div className="mb-6">
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Catégorie</p>
            <div className="flex flex-col gap-1">
              {FALLBACK_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`filter-cat-${c.id}`}
                  onClick={() => setFilter("category", category === c.id ? "" : c.id)}
                  className={`text-left text-sm px-2 py-1.5 rounded-sm transition-colors ${category === c.id ? "bg-foreground text-background" : "hover:bg-muted"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Ville</p>
            <div className="flex flex-wrap gap-1.5">
              {FALLBACK_CITIES.map((ct) => (
                <button
                  key={ct}
                  data-testid={`filter-city-${ct}`}
                  onClick={() => setFilter("city", city === ct ? "" : ct)}
                  className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${city === ct ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
                >
                  {ct}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Results */}
        <main className="lg:col-span-9">
          {loading ? (
            <div className="text-center font-mono text-sm uppercase tracking-wider text-muted-foreground py-20">Chargement...</div>
          ) : artists.length === 0 ? (
            <div className="card-mc p-12 text-center" data-testid="no-results">
              <p className="font-display font-black text-2xl mb-2">{t("explore.no_results_title", "Aucun artiste trouvé")}</p>
              <p className="text-muted-foreground mb-6">{t("explore.no_results_desc", "Essayez d'élargir vos filtres ou inscrivez-vous comme artiste pour rejoindre la plateforme.")}</p>
              <Link to="/register" className="btn-primary">Inscription artistes</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {artists.map((a) => <ArtistCard key={a.id} artist={a} />)}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
